/**
 * Recognises the backend's rate-limit answers.
 *
 * The verification and password-reset email limits answer with
 * `{ success: false, error: "Too many ..." }` (or throw a GraphQL error with
 * that message), and the API Worker's own limiter answers HTTP 429. The auth
 * pages fall back to Firebase's built-in emails when our backend can't send;
 * doing that on a rate limit sent the email anyway and made the limit
 * pointless, so callers check this first and only fall back when it is null.
 */
import { getApolloErrorMessage } from './apolloError';

/** Shown when the limit came back without customer-facing text (a bare 429). */
export const GENERIC_RATE_LIMIT_MESSAGE =
  'Too many requests. Please wait a few minutes and try again.';

const firstLine = (text: string) => text.split('\n')[0].trim();

export const isRateLimitMessage = (message?: string | null): boolean => {
  if (typeof message !== 'string') return false;
  const text = message.trim();
  if (!text) return false;
  return (
    /^too many\b/i.test(text) ||
    /^rate limit/i.test(text) ||
    // What a wrapped ApolloError says when the Worker answers 429.
    /\bstatus code 429\b/.test(text)
  );
};

/**
 * The message to show for the first rate-limit text among `messages` (e.g. a
 * response's `error` and `message` fields), or null if none is a rate limit.
 */
export const findRateLimitMessage = (
  ...messages: Array<string | null | undefined>
): string | null => {
  const hit = messages.find((message) => isRateLimitMessage(message));
  if (!hit) return null;
  const text = firstLine(hit);
  // The backend's "Too many ..." copy is written for customers; a transport
  // message like "Received status code 429" is not.
  return /^too many\b/i.test(text) ? text : GENERIC_RATE_LIMIT_MESSAGE;
};

/**
 * The same check for a thrown error: an ApolloError (graphQLErrors[0] or a 429
 * network error) or the plain Error that graphqlService rethrows with the
 * Apollo message.
 */
export const rateLimitMessageFromError = (error: unknown): string | null => {
  if (!error) return null;
  const candidate = error as {
    message?: unknown;
    networkError?: { statusCode?: unknown } | null;
  };
  if (candidate.networkError?.statusCode === 429) return GENERIC_RATE_LIMIT_MESSAGE;
  return findRateLimitMessage(
    getApolloErrorMessage(error),
    typeof candidate.message === 'string' ? candidate.message : null
  );
};
